import axios from "axios";
import { getTeacherId } from "./teacher";
import {
  clearPendingGenerate,
  clearPendingRefine,
  clearPendingStagedBuild,
  clearPendingUnitTask,
  setPendingGenerate,
  setPendingRefine,
  setPendingStagedBuild,
  setPendingUnitTask,
} from "./inProgress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Send cookies on every call so the backend can identify a logged-in user via
// the session_token cookie. Anonymous flow still works because endpoints
// accept an explicit teacher_id query param too.
export const api = axios.create({
  baseURL: API,
  timeout: 120000,
  withCredentials: true,
});

// Phase 96 — Hard paywall flow. Whenever the backend says 402 ("pick
// a plan") or 423 ("pre-launch is full"), bounce the user to /pricing
// so they always see the next step. The toast still fires from the
// call site; the redirect happens here, centrally, so no individual
// page has to remember to do it.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    if (
      (status === 402 || status === 423) &&
      path !== "/pricing" &&
      path !== "/billing/success" &&
      !path.startsWith("/auth/")
    ) {
      // Defer the navigation so the call-site toast still renders.
      setTimeout(() => {
        try {
          window.location.assign("/pricing");
        } catch {
          /* SSR / sandboxed iframe — ignore */
        }
      }, 1200);
    }
    return Promise.reject(error);
  },
);

// Feb 10 2026 — real-time progress wire.
// `onProgress(message)` fires whenever the polled job doc's `progress`
// string changes (backend writes via `jobs.mark_progress`). Callers
// use it to surface backend waypoints in wait cards. The callback is
// opt-in — every existing call site keeps working unchanged.
//
// Contract (locked by Jest tripwires):
//  * Fires only when `job.progress` is a non-empty string.
//  * Never fires twice for the same message (dedup by strict equality).
//  * Never fires once the terminal `done` frame has arrived.
//  * Never breaks the outer loop if the callback throws.
export const pollJobUntilDone = async ({
  jobId,
  pollUrl,
  initialDelay = 4000,
  interval = 4000,
  timeoutMs = 5 * 60 * 1000,
  errorPrefix = "Job",
  onProgress,
}) => {
  const started_at = Date.now();
  await new Promise((r) => setTimeout(r, initialDelay));
  // Tolerate transient network blips during polling — a single failed
  // GET (timeout, 502 from ingress, momentary disconnect) used to throw
  // out of the whole loop and surface as a "Network Error" toast even
  // though the backend was still happily working. We now retry up to
  // 3 consecutive failures with a short backoff. The total 5-minute
  // timeout still applies so a genuinely dead job is still surfaced.
  let consecutiveErrors = 0;
  const maxConsecutiveErrors = 3;
  let lastProgress = null;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (Date.now() - started_at > timeoutMs) {
      throw new Error(`${errorPrefix} timed out after 5 minutes`);
    }
    try {
      const { data: job } = await api.get(pollUrl, { timeout: 20000 });
      consecutiveErrors = 0;
      // Surface any new backend waypoint BEFORE we short-circuit on
      // terminal state, so a "done" frame that also carries a final
      // progress note still lands (belt + braces; today the backend
      // clears progress on completion, but the contract shouldn't
      // depend on that).
      if (
        typeof onProgress === "function" &&
        typeof job.progress === "string" &&
        job.progress.trim() &&
        job.progress !== lastProgress &&
        job.status !== "done"
      ) {
        lastProgress = job.progress;
        try {
          onProgress(job.progress);
        } catch {
          /* never break the poll loop because a caller's callback threw */
        }
      }
      if (job.status === "done") return job;
      if (job.status === "error") {
        const err = new Error(job.error || `${errorPrefix} failed`);
        err.status = job.status_code;
        throw err;
      }
    } catch (err) {
      // A `status === "error"` job response above re-throws here — let
      // it propagate, that's a real backend failure, not a network blip.
      if (err?.status || err?.response?.data?.status === "error") {
        throw err;
      }
      consecutiveErrors += 1;
      if (consecutiveErrors >= maxConsecutiveErrors) {
        throw err;
      }
      // Short backoff before retrying — keeps total elapsed time
      // bounded by the 5-minute outer timeout.
    }
    await new Promise((r) => setTimeout(r, interval));
  }
};

export const generateActivity = async (payload, { onJobStarted } = {}) => {
  const { data: started } = await api.post(
    "/generate-activity",
    { ...payload, teacher_id: getTeacherId() },
    { timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingGenerate(jobId);
  // Expose the jobId to the caller so it can resume polling if the
  // inner promise later rejects with a transient error.
  try {
    onJobStarted?.(jobId);
  } catch {
    /* never break generation because a caller's callback threw */
  }
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/generate-activity/jobs/${jobId}`,
      errorPrefix: "Generation",
    });
    return job.activity;
  } finally {
    clearPendingGenerate();
  }
};

// Resume polling a generate job whose id we saved before tab close/refresh.
export const resumeGenerateJob = async (jobId) => {
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      initialDelay: 0,
      errorPrefix: "Generation",
    });
    return job.activity;
  } finally {
    clearPendingGenerate();
  }
};

// Phase 85a — Summative Task Designer. Same async-job pattern as
// generateActivity but hits the multi-expectation summative endpoint
// (Standard tier and up). Backend rejects requests with <2 expectations
// or a num_lessons outside the 2-10 range.
export const generateSummativeTask = async (payload) => {
  const { data: started } = await api.post(
    "/generate-summative-task",
    { ...payload, teacher_id: getTeacherId() },
    { timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingGenerate(jobId);
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Summative task",
    });
    return job.activity;
  } finally {
    clearPendingGenerate();
  }
};

export const getSkillsBreakdown = async (planId) => {
  const { data } = await api.get(`/skills-breakdown/${planId}`);
  return data;
};

// Coverage Plan pre-call (Stage 1.5) — generates the culminating task AND the
// skills breakdown (~60-90s), so it returns a job_id immediately and we poll
// /api/jobs/{id} (same resilient pattern as the build). The completed job doc
// carries `no_breakdown` | `blocked` | (`task_activity_id`, `breakdown`,
// `summary`). Only meaningful for standalone/distributed.
export const previewSkillsBreakdown = async (payload, { onProgress } = {}) => {
  const { data: started } = await api.post(
    "/skills-breakdown/preview",
    { ...payload, teacher_id: getTeacherId() },
    { timeout: 30000 },
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  const job = await pollJobUntilDone({
    jobId,
    pollUrl: `/jobs/${jobId}`,
    errorPrefix: "Coverage plan",
    onProgress,
    initialDelay: 3000,
    interval: 4000,
  });
  return job;
};

// Stage B — staged unit build. One resilient background job that drafts
// the culminating task, then generates task-aware lessons. Returns a
// job_id immediately; we poll /api/jobs/{id}. The completed job doc
// carries top-level `blocked`, `task_activity_id`, `unit_activity_id`,
// `model`, and `timings` (see staged_build.run_staged_build_job).
export const startStagedUnitBuild = async (
  payload,
  { onProgress, onJobStarted } = {},
) => {
  const { data: started } = await api.post(
    "/staged-unit-build",
    { ...payload, teacher_id: getTeacherId() },
    { timeout: 30000 },
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingStagedBuild(jobId, {
    estimateSeconds: started?.estimate_seconds,
    model: payload?.model,
    numLessons: payload?.num_lessons,
  });
  try {
    onJobStarted?.(jobId, started?.estimate_seconds);
  } catch {
    /* never break the build because a caller's callback threw */
  }
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Unit build",
      onProgress,
      initialDelay: 3000,
      interval: 4000,
    });
    return job;
  } finally {
    clearPendingStagedBuild();
  }
};

// Resume polling a staged build whose id we saved before tab close/refresh.
export const resumeStagedUnitBuild = async (jobId, { onProgress } = {}) => {
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      initialDelay: 0,
      errorPrefix: "Unit build",
      onProgress,
    });
    return job;
  } finally {
    clearPendingStagedBuild();
  }
};

// Retire-the-bridge — build + attach the Standalone culminating task IN PLACE
// for a finished unit, grounded in its taught lessons. Returns the completed
// job doc (top-level task_activity_id / unit_activity_id, or blocked +
// block_reason). Job id is persisted per-unit so a reload resumes the card.
export const buildUnitStandaloneTaskInPlace = async (
  unitActivityId,
  body,
  { onProgress, onJobStarted } = {},
) => {
  const { data: started } = await api.post(
    `/unit-summative/${unitActivityId}/build-standalone-task`,
    body || {},
    { timeout: 30000 },
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingUnitTask(unitActivityId, jobId, started?.estimate_seconds);
  try {
    onJobStarted?.(jobId, started?.estimate_seconds);
  } catch {
    /* never break the build because a caller's callback threw */
  }
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Task build",
      onProgress,
      initialDelay: 2500,
      interval: 3500,
    });
    return job;
  } finally {
    clearPendingUnitTask(unitActivityId);
  }
};

export const resumeUnitStandaloneTask = async (unitActivityId, jobId, { onProgress } = {}) => {
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      initialDelay: 0,
      errorPrefix: "Task build",
      onProgress,
    });
    return job;
  } finally {
    clearPendingUnitTask(unitActivityId);
  }
};
export const listMyMathUnits = async () => (await api.get("/math-engine/my-units")).data;
export const deleteMyMathUnit = (unitId) =>
  api
    .delete(`/math-engine/my-units/${encodeURIComponent(unitId)}`)
    .then((r) => r.data);
export const getMathUnitProgress = async (unitId) =>
  (await api.get(`/math-engine/my-units/${encodeURIComponent(unitId)}`)).data;
export const markMathMidQuizOffered = async (unitId) =>
  (await api.post("/math-engine/units/mark-offered", { unit_id: unitId })).data;
export const dismissMathMidQuiz = async (unitId) =>
  (await api.post("/math-engine/units/dismiss-mid-quiz", { unit_id: unitId })).data;
export const generateMathMidQuiz = async (unitId, questionCount = 12) =>
  (await api.post("/math-engine/units/mid-quiz", { unit_id: unitId, question_count: questionCount })).data;
export const generateMathEndUnitTest = async (unitId, questionCount = 25) =>
  (await api.post("/math-engine/units/end-test", { unit_id: unitId, question_count: questionCount })).data;
export const recordMathUnitWrapup = async (unitId, kind, activityId = null) =>
  (await api.post("/math-engine/units/record-wrapup", { unit_id: unitId, kind, activity_id: activityId })).data;
export const recordMathMidQuizResult = async (unitId, summary, remediation = null) => {
  const body = { unit_id: unitId, summary };
  // When `remediation` is null we let the backend derive a structured
  // list via Claude. When an explicit array is passed we trust it.
  if (Array.isArray(remediation)) body.remediation = remediation;
  return (await api.post("/math-engine/units/mid-quiz-result", body)).data;
};

// Phase 105.3 — Per-row edits in the Adaptive Plan Preview. Patches
// a single remediation row's status and/or note. Backend returns the
// full updated remediation list so the caller can swap optimistic
// state with the validated truth (the server may have re-coerced
// the status if the chip's value was illegal).
export const patchMathRemediationRow = async (unitId, code, { status, note } = {}) => {
  const body = { unit_id: unitId, code };
  if (status !== undefined) body.status = status;
  if (note !== undefined) body.note = note;
  return (await api.patch("/math-engine/units/remediation-row", body)).data;
};

// Emergency supply plan — same async-job pattern as generateActivity but hits
// the supply-plan-specific endpoint with its bare-bones prompt.
export const generateSupplyPlan = async (payload) => {
  const { data: started } = await api.post(
    "/generate-supply-plan",
    { ...payload, teacher_id: getTeacherId() },
    { timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingGenerate(jobId);
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Supply plan",
    });
    return job.activity;
  } finally {
    clearPendingGenerate();
  }
};

export const listActivities = () =>
  api.get("/activities", { params: { teacher_id: getTeacherId() } }).then((r) => r.data);

export const getActivity = (id) =>
  api.get(`/activities/${encodeURIComponent(id)}`).then((r) => r.data);

// Child lessons of a unit (those generated from the unit's prompt cards).
export const listUnitLessons = (unitId) =>
  api
    .get(`/activities/by-unit/${encodeURIComponent(unitId)}`, {
      params: { teacher_id: getTeacherId() },
    })
    .then((r) => r.data);

// units-on-the-store — set a unit lesson's skill + focus area (or clear both).
export const patchUnitLessonSkill = (unitId, body) =>
  api
    .patch(`/activities/${encodeURIComponent(unitId)}/unit-lessons/skill`, body, {
      params: { teacher_id: getTeacherId() },
    })
    .then((r) => r.data);

// Phase 119.E series-builder — return the ordered chain of activities
// sharing this activity's series_root_id (or an empty chain if it
// isn't part of a series).
export const getActivitySeries = (activityId) =>
  api.get(`/activities/${encodeURIComponent(activityId)}/series`).then((r) => r.data);

// Phase 127 — In-app preview. Returns the cached structured slide_deck_spec
// the PPTX builder reads from, so HTML thumbnails render from the same
// source as the export. Generates on first call.
export const getSlideSpec = (activityId, { regenerate = false } = {}) =>
  api
    .get(`/activities/${encodeURIComponent(activityId)}/slide-spec`, {
      params: regenerate ? { regenerate: true } : {},
      timeout: 60000,
    })
    .then((r) => r.data);

// Phase 127 — Records which next-step the teacher chose after previewing
// (Export vs. Open in Canva), per artifact type. No content, no student
// data — teacher-action telemetry only.
export const logPreviewEvent = (activityId, { action, artifact }) =>
  api
    .post(`/activities/${encodeURIComponent(activityId)}/preview-event`, {
      action,
      artifact,
    })
    .then((r) => r.data)
    .catch(() => null); // fire-and-forget — never fail the UX

// Bulk-archive a unit (parent + all child lessons) to a single Drive
// sub-folder under "AtomicEdu Lessons". Format applies to every child
// lesson. Returns `{folder_id, folder_url, saved: [...], failed: [...]}`.
export const saveUnitToDrive = (unitId, format = "package") =>
  api
    .post(`/activities/${encodeURIComponent(unitId)}/save-unit-to-drive`, {
      format,
    })
    .then((r) => r.data);

// ===== Long Range Plan (Phase 112) =====
export const getLongRangePlan = (schoolYear) =>
  api
    .get("/long-range-plan", schoolYear ? { params: { school_year: schoolYear } } : undefined)
    .then((r) => r.data);

export const saveLongRangePlan = (schoolYear, periods) =>
  api
    .put("/long-range-plan", { school_year: schoolYear, periods })
    .then((r) => r.data);

export const listLrpYears = () =>
  api.get("/long-range-plan/years").then((r) => r.data);

export const getLrpSuggestions = (grade) =>
  api
    .get("/long-range-plan/suggestions", grade ? { params: { grade } } : undefined)
    .then((r) => r.data);

// Read-only feed of summative-task chips for the Planner.
// Returns `{chips: [{date, unit_name, subject, type, period, unit_id}]}`.
export const getLrpSummativeChips = (start, end) =>
  api
    .get("/long-range-plan/summative-chips", { params: { start, end } })
    .then((r) => r.data);

export const deleteActivity = (id) =>
  api
    .delete(`/activities/${id}`, { params: { teacher_id: getTeacherId() } })
    .then((r) => r.data);

// ---------- Community Sharing (Phase 92) ----------
export const listCommunityLessons = async (filters = {}) => {
  const params = {};
  ["grade", "subject", "skill", "type", "q", "limit", "offset"].forEach((k) => {
    const v = filters[k];
    if (v !== undefined && v !== null && v !== "") params[k] = v;
  });
  return (await api.get("/community/lessons", { params })).data;
};

export const getCommunityLesson = async (activityId) =>
  (await api.get(`/community/lessons/${encodeURIComponent(activityId)}`)).data;

export const importCommunityLesson = async (activityId) =>
  (await api.post("/community/use", { activity_id: activityId })).data;

export const shareActivity = async (activityId, displayName = "") =>
  (await api.post("/community/share", {
    activity_id: activityId,
    display_name: displayName || null,
  })).data;

export const unshareActivity = async (activityId) =>
  (await api.post("/community/unshare", { activity_id: activityId })).data;

export const fetchCommunityRecentTicker = async (limit = 10) =>
  (await api.get("/community/recent-ticker", { params: { limit } })).data;

export const fetchUnseenPickups = async () =>
  (await api.get("/community/pickups")).data;

export const markPickupsSeen = async (ids) =>
  (await api.post("/community/pickups/seen", { ids })).data;

// ---------- Public Share Links (Feb 2026) ----------
// Generate a public, no-auth URL teachers can post on social media.
// Distinct from Community sharing (which is gated to other paid users).
export const getPublicShareStatus = async (activityId) =>
  (await api.get(`/activities/${encodeURIComponent(activityId)}/public-share`)).data;

export const enablePublicShare = async (activityId) =>
  (await api.post(`/activities/${encodeURIComponent(activityId)}/public-share`)).data;

export const disablePublicShare = async (activityId) =>
  (await api.delete(`/activities/${encodeURIComponent(activityId)}/public-share`)).data;

// ---------- Admin email blasts (Feb 2026) ----------
// Bulk-email all registered users from the admin panel. Each recipient
// gets their own message — no BCC leakage of other addresses.
export const adminEmailAudience = async () =>
  (await api.get("/admin/email/audience")).data;

export const adminEmailBlast = async ({ subject, body_markdown }) =>
  (await api.post("/admin/email/blast", { subject, body_markdown })).data;

export const adminEmailBlasts = async (limit = 20) =>
  (await api.get("/admin/email/blasts", { params: { limit } })).data;

export const adminEmailBlast_get = async (blastId) =>
  (await api.get(`/admin/email/blasts/${encodeURIComponent(blastId)}`)).data;


// ---------- Contact / Feedback / Deletion (Phase 93) ----------
export const submitContactForm = async ({ name, email, message, topic }) =>
  (await api.post("/contact", { name, email, message, topic: topic || null })).data;

export const submitInAppFeedback = async ({ message, context }) =>
  (await api.post("/contact/feedback", { message, context: context || null })).data;

export const submitDeletionRequest = async ({ reason }) =>
  (await api.post("/contact/deletion", { reason: reason || null })).data;

// ---------- Beta access (Phase 93) ----------
// getBetaStatus removed Feb 2026 launch — pre-launch UI no longer rendered.
export const redeemBetaCode = async (code) =>
  (await api.post("/beta/redeem", { code })).data;
export const joinLaunchWaitlist = async (email, source = "pricing_page") =>
  (await api.post("/waitlist/join", { email, source })).data;

// ---------- Folders (Phase 49) ----------
export const listFolders = () =>
  api.get("/folders", { params: { teacher_id: getTeacherId() } }).then((r) => r.data);

export const createFolder = (name, color, opts = {}) =>
  api
    .post("/folders", {
      teacher_id: getTeacherId(),
      name,
      color,
      // Phase 121 — hierarchy
      kind: opts.kind || "subject",
      parent_folder_id: opts.parentFolderId || null,
      subject_name: opts.subjectName || null,
      unit_activity_id: opts.unitActivityId || null,
    })
    .then((r) => r.data);

// Phase 121 — hierarchy helpers
export const updateFolderHierarchy = (folderId, updates) =>
  api
    .patch(
      `/folders/${folderId}`,
      {
        name: updates.name,
        color: updates.color,
        parent_folder_id: updates.parentFolderId,
        subject_name: updates.subjectName,
        unit_activity_id: updates.unitActivityId,
      },
      { params: { teacher_id: getTeacherId() } }
    )
    .then((r) => r.data);

export const getFolderBreadcrumb = (folderId) =>
  api
    .get(`/folders/${folderId}/breadcrumb`, {
      params: { teacher_id: getTeacherId() },
    })
    .then((r) => r.data);

export const getFolderContents = (folderId) =>
  api
    .get(`/folders/${folderId}/contents`, {
      params: { teacher_id: getTeacherId() },
    })
    .then((r) => r.data);

export const deleteFolderWithCascade = (folderId, cascade = "unfile") =>
  api
    .delete(`/folders/${folderId}`, {
      params: { teacher_id: getTeacherId(), cascade },
    })
    .then((r) => r.data);

export const autoFileActivity = (activityId) =>
  api
    .post(
      `/activities/${activityId}/auto-file`,
      null,
      { params: { teacher_id: getTeacherId() } }
    )
    .then((r) => r.data);

export const moveActivityToFolder = (activityId, folderId) =>
  api
    .patch(
      `/activities/${activityId}/folder`,
      { folder_id: folderId },
      { params: { teacher_id: getTeacherId() } }
    )
    .then((r) => r.data);

export const updateFolder = (folderId, { name, color } = {}) =>
  api
    .patch(
      `/folders/${folderId}`,
      { name, color },
      { params: { teacher_id: getTeacherId() } }
    )
    .then((r) => r.data);

export const deleteFolder = (folderId) =>
  api
    .delete(`/folders/${folderId}`, { params: { teacher_id: getTeacherId() } })
    .then((r) => r.data);

// Move a folder under a new parent Subject (or to top-level when
// parentFolderId is null). Backend PATCH /folders/{id} enforces the
// 2-level Subject→Unit invariant.
export const moveFolder = (folderId, parentFolderId) =>
  api
    .patch(
      `/folders/${folderId}`,
      { parent_folder_id: parentFolderId ?? null },
      { params: { teacher_id: getTeacherId() } }
    )
    .then((r) => r.data);

export const reorderFolders = (orderedIds) =>
  api
    .patch("/folders/reorder", {
      order: orderedIds,
      teacher_id: getTeacherId(),
    })
    .then((r) => r.data);


// ---------- Math Engine (Session 1 + 2) ----------
export const checkMathEngineAccess = () =>
  api.get("/math-engine/access").then((r) => r.data);

export const listMathEngineUnits = (grade) =>
  api.get("/math-engine/units", { params: { grade } }).then((r) => r.data);

// Ontario Ministry-published sample task lookup powering the Coverage
// Tracker popover ("Show me the Ministry sample task for B1.4").
// Returns `null` when the Ministry doc has no published task for the
// requested code (404 → null, all other errors re-throw).
export const fetchMinistrySampleTask = (grade, code) =>
  api
    .get("/math-engine/ministry-task", { params: { grade, code } })
    .then((r) => r.data)
    .catch((err) => {
      if (err?.response?.status === 404) return null;
      throw err;
    });

// In-flight workflows for the Resume panel.
export const listMathEngineInFlight = () =>
  api.get("/math-engine/in-flight").then((r) => r.data?.workflows || []);

// Rehydrate the full artifact set for a (grade, unit, mini) workflow.
export const resumeMathEngineWorkflow = ({ grade, unit_number, mini_unit_label }) =>
  api
    .post("/math-engine/resume", {
      grade,
      unit_number,
      mini_unit_label: mini_unit_label || null,
    })
    .then((r) => r.data?.artifacts || {});

// Delete every Math Engine artifact for one workflow — used by the
// "Pick up where you left off" panel to remove a stale / abandoned unit.
export const deleteMathEngineWorkflow = ({ grade, unit_number, mini_unit_label }) =>
  api
    .post("/math-engine/delete-workflow", {
      grade,
      unit_number,
      mini_unit_label: mini_unit_label || null,
    })
    .then((r) => r.data);

// Session 2 — kick off the Math Engine for a unit and poll the resulting
// background job. `mode` is "activation_only" (Session 2 only generates
// the activation activity) or "start_unit" (placeholder until Session 3
// chains diagnostic + lesson 1 — also returns the activation today).
export const startMathEngine = async ({
  grade,
  unit_number,
  mini_unit_label,
  mode = "activation_only",
}, { onProgress } = {}) => {
  const { data: started } = await api.post(
    "/math-engine/start",
    {
      grade,
      unit_number,
      mini_unit_label: mini_unit_label || null,
      mode,
      teacher_id: getTeacherId(),
    },
    { timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingGenerate(jobId);
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Math Engine",
      onProgress,
    });
    return job.activity;
  } finally {
    clearPendingGenerate();
  }
};

// Session 4 — Stage 3 Adaptive Lesson 1. `results` is an array of 5
// objects, one per diagnostic question: { A, B, C, D } percentages.
export const startMathLesson1 = async ({
  grade,
  unit_number,
  mini_unit_label,
  after_diagnostic_id,
  results,
}, { onProgress } = {}) => {
  const { data: started } = await api.post(
    "/math-engine/lesson1",
    {
      grade,
      unit_number,
      mini_unit_label: mini_unit_label || null,
      after_diagnostic_id,
      results,
      teacher_id: getTeacherId(),
    },
    { timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingGenerate(jobId);
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Math Engine lesson 1",
      onProgress,
    });
    return job.activity;
  } finally {
    clearPendingGenerate();
  }
};

// Session 6 — Stage 6 Adaptive Lesson 2. Branches on the Stage 5
// recommendation stored on the referenced exit-review activity.
export const startMathLesson2 = async ({
  grade,
  unit_number,
  mini_unit_label,
  after_exit_review_id,
  lesson_index,
}, { onProgress } = {}) => {
  const { data: started } = await api.post(
    "/math-engine/lesson2",
    {
      grade,
      unit_number,
      mini_unit_label: mini_unit_label || null,
      after_exit_review_id,
      lesson_index: lesson_index || null,
      teacher_id: getTeacherId(),
    },
    { timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingGenerate(jobId);
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Math Engine lesson 2",
      onProgress,
    });
    return job.activity;
  } finally {
    clearPendingGenerate();
  }
};

// Session 7 — End-of-unit summary. Aggregates the full lesson chain
// + exit reviews into a printable retrospective.
export const startMathUnitSummary = async ({
  grade,
  unit_number,
  mini_unit_label,
}, { onProgress } = {}) => {
  const { data: started } = await api.post(
    "/math-engine/unit-summary",
    {
      grade,
      unit_number,
      mini_unit_label: mini_unit_label || null,
      teacher_id: getTeacherId(),
    },
    { timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingGenerate(jobId);
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Math Engine unit summary",
      onProgress,
    });
    return job.activity;
  } finally {
    clearPendingGenerate();
  }
};

// Session 5 — Stage 5 Exit Ticket Review. Three buckets + optional
// note → engine computes the call and Claude writes the next-day plan.
export const startMathExitReview = async ({
  grade,
  unit_number,
  mini_unit_label,
  after_lesson_id,
  mastered_pct,
  developing_pct,
  reteach_pct,
  note,
}, { onProgress } = {}) => {
  const { data: started } = await api.post(
    "/math-engine/exit-review",
    {
      grade,
      unit_number,
      mini_unit_label: mini_unit_label || null,
      after_lesson_id,
      mastered_pct,
      developing_pct,
      reteach_pct,
      note: note || "",
      teacher_id: getTeacherId(),
    },
    { timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingGenerate(jobId);
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Math Engine exit review",
      onProgress,
    });
    return job.activity;
  } finally {
    clearPendingGenerate();
  }
};

// Session 3 — Stage 2 diagnostic generation. When `after_activity_id`
// is supplied, the diagnostic seeds Claude with the activation's
// predicted misconceptions so distractors target them precisely.
export const startMathDiagnostic = async ({
  grade,
  unit_number,
  mini_unit_label,
  after_activity_id,
}, { onProgress } = {}) => {
  const { data: started } = await api.post(
    "/math-engine/diagnostic",
    {
      grade,
      unit_number,
      mini_unit_label: mini_unit_label || null,
      after_activity_id: after_activity_id || null,
      teacher_id: getTeacherId(),
    },
    { timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingGenerate(jobId);
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Math Engine diagnostic",
      onProgress,
    });
    return job.activity;
  } finally {
    clearPendingGenerate();
  }
};

export const getCoverage = () =>
  api.get("/coverage", { params: { teacher_id: getTeacherId() } }).then((r) => r.data);

export const getSkillInTheWild = () =>
  api.get("/skill-in-the-wild").then((r) => r.data);

export const requestMagicLink = (email) =>
  api.post("/auth/email/request", { email }).then((r) => r.data);

export const generateReportCardComment = (payload) =>
  api
    .post(
      "/report-cards/generate",
      { teacher_id: getTeacherId(), ...payload },
      { timeout: 60000 }
    )
    .then((r) => r.data);

// Phase 46 — Auto-populated Report Card Builder
// Phase 83c — `taughtOnly` filters the source activities to lessons
// scheduled on the planner with a date ≤ today.
export const getReportCardBuilderData = (studentGrade = "", taughtOnly = false) =>
  api
    .get("/report-cards/builder-data", {
      params: {
        teacher_id: getTeacherId(),
        ...(studentGrade ? { student_grade: studentGrade } : {}),
        ...(taughtOnly ? { taught_only: true } : {}),
      },
    })
    .then((r) => r.data);

export const generateReportCardBuilderComment = (payload) =>
  api
    .post(
      "/report-cards/builder-generate",
      { teacher_id: getTeacherId(), ...payload },
      { timeout: 60000 }
    )
    .then((r) => r.data);

// Phase 113.A — LRP context panel feed. Subject + report-type keyed.
// Returns `{ subject, report_type, units: [...], has_lrp: bool }`.
// Auto-refreshes whenever the teacher switches subject tabs.
export const getRcbLrpContext = (subject, reportType) =>
  api
    .get("/report-cards/lrp-context", {
      params: {
        subject,
        report_type: reportType,
        teacher_id: getTeacherId(),
      },
    })
    .then((r) => r.data);

// Phase 113.C — Recently-taught expectations feed for the Report Card
// Builder. Returns the 2-3 most recently confirmed-taught expectation
// codes for the given RCB subject, so the picker surfaces them at the
// top with a faint "recently taught" badge.
export const getRcbRecentlyTaughtExpectations = (subject, limit = 3) =>
  api
    .get("/report-cards/recently-taught-expectations", {
      params: { subject, limit, teacher_id: getTeacherId() },
    })
    .then((r) => r.data);

// Phase 113.D — "Schedule remaining lessons" quick-action.
// Two-step propose → commit flow so the platform proposes a placement
// for every unscheduled lesson in a unit, then the teacher reviews
// and confirms before anything is written to the Planner.
export const proposeScheduleForUnit = (unitActivityId, startDate) =>
  api
    .get("/planner/propose-schedule", {
      params: {
        unit_activity_id: unitActivityId,
        ...(startDate ? { start_date: startDate } : {}),
      },
    })
    .then((r) => r.data);

export const commitScheduleForUnit = (unitActivityId, placements) =>
  api
    .post("/planner/commit-schedule", {
      unit_activity_id: unitActivityId,
      placements,
    })
    .then((r) => r.data);

export const listUnitsWithUnscheduledLessons = () =>
  api.get("/planner/units-with-unscheduled-lessons").then((r) => r.data);

// Phase 114.A — Gradebook ----------------------------------------------
export const getGradebookSetupStatus = (schoolYear) =>
  api
    .get("/gradebook/setup-status", {
      params: schoolYear ? { school_year: schoolYear } : {},
    })
    .then((r) => r.data);

export const getGradebookStudents = (schoolYear) =>
  api
    .get("/gradebook/students", {
      params: schoolYear ? { school_year: schoolYear } : {},
    })
    .then((r) => r.data);

export const saveGradebookStudents = (payload) =>
  api.post("/gradebook/students", payload).then((r) => r.data);

export const getGradebookUnits = (schoolYear) =>
  api
    .get("/gradebook/units", {
      params: schoolYear ? { school_year: schoolYear } : {},
    })
    .then((r) => r.data);

export const listGradebookAssessments = (unitId) =>
  api
    .get("/gradebook/assessments", { params: { unit_id: unitId } })
    .then((r) => r.data);

export const createGradebookAssessment = (payload) =>
  api.post("/gradebook/assessments", payload).then((r) => r.data);

export const deleteGradebookAssessment = (assessmentId) =>
  api.delete(`/gradebook/assessments/${assessmentId}`).then((r) => r.data);

export const listGradebookResults = ({ unitId, assessmentId }) =>
  api
    .get("/gradebook/results", {
      params: assessmentId
        ? { assessment_id: assessmentId }
        : { unit_id: unitId },
    })
    .then((r) => r.data);

export const upsertGradebookResult = (payload) =>
  api.put("/gradebook/results", payload).then((r) => r.data);

// Phase 114.B — Per-Student Rubric Panel + AI feedback.
export const getGradebookSingleResult = (assessmentId, studentNumber) =>
  api
    .get(`/gradebook/results/${assessmentId}/${studentNumber}`)
    .then((r) => r.data);

export const generateGradebookFeedback = ({
  assessment_id,
  student_number,
  tone,
}) =>
  api
    .post("/gradebook/results/feedback", {
      assessment_id,
      student_number,
      tone,
    })
    .then((r) => r.data);

export const gradebookRubricPdfUrl = (assessmentId, studentNumber) => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/gradebook/rubric-pdf?assessment_id=${encodeURIComponent(
    assessmentId
  )}&student_number=${studentNumber}`;
};

export const gradebookRubricPdfClassUrl = (assessmentId) => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/gradebook/rubric-pdf/class?assessment_id=${encodeURIComponent(
    assessmentId
  )}`;
};

// Phase 114.C — Coverage Tracker → Summative Task Builder bridge.
export const listSummativeSuggestions = () =>
  api.get("/coverage/summative-suggestions").then((r) => r.data);

export const dismissSummativeSuggestion = ({
  subject,
  grade,
  strand_code,
  reporting_period,
}) =>
  api
    .post("/coverage/summative-suggestions/dismiss", {
      subject,
      grade,
      strand_code,
      reporting_period,
    })
    .then((r) => r.data);

export const undismissSummativeSuggestion = ({
  subject,
  grade,
  strand_code,
  reporting_period,
}) =>
  api
    .delete("/coverage/summative-suggestions/dismiss", {
      params: { subject, grade, strand_code, reporting_period },
    })
    .then((r) => r.data);

// Phase 114.D.1 — Unit Summative Task fork.
export const getUnitSummative = (unitActivityId) =>
  api.get(`/unit-summative/${unitActivityId}`).then((r) => r.data);

export const listUnitSummatives = () =>
  api.get("/unit-summative").then((r) => r.data);

export const startUnitSummative = (unitActivityId, payload) =>
  api
    .post(`/unit-summative/${unitActivityId}/start`, payload)
    .then((r) => r.data);

export const switchUnitSummativeModel = (unitActivityId, payload) =>
  api
    .post(`/unit-summative/${unitActivityId}/switch`, payload)
    .then((r) => r.data);

export const attachUnitSummativeTask = (unitActivityId, payload) =>
  api
    .post(`/unit-summative/${unitActivityId}/attach-task`, payload)
    .then((r) => r.data);

export const deleteUnitSummative = (unitActivityId) =>
  api.delete(`/unit-summative/${unitActivityId}`).then((r) => r.data);

// Phase 114.D.2 — Distributed (Model B) planner save.
export const saveDistributedComponents = (unitActivityId, payload) =>
  api
    .post(`/unit-summative/${unitActivityId}/save-components`, payload)
    .then((r) => r.data);

export const distributedTaskOverviewPdfUrl = (unitActivityId) => {
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}/api/unit-summative/${unitActivityId}/overview-pdf`;
};

// Phase 108 — Live Reporting taught layer.
export const markActivityTaught = (activityId, { cascade = false } = {}) =>
  api
    .post(`/activities/${activityId}/taught`, { cascade })
    .then((r) => r.data);

export const unmarkActivityTaught = (activityId) =>
  api.delete(`/activities/${activityId}/taught`).then((r) => r.data);

// Phase 109.D — What's New banner. Surfaces the most recent user-facing
// phase from PRD.md to paid teachers; free / signed-out users get [].
export const fetchWhatsNew = () =>
  api.get("/whats-new").then((r) => r.data);

export const previewTaughtCascade = (activityId) =>
  api.get(`/activities/${activityId}/cascade-preview`).then((r) => r.data);

// Admin · Product Spec Sheet (Feb 2026).
export const adminSpecSheet = () =>
  api.get("/admin/spec-sheet").then((r) => r.data);

// Direct URL to the branded PDF — opened in a new tab so the session
// cookie authenticates the download.
export const adminSpecSheetPdfUrl = () => `${API}/admin/spec-sheet.pdf`;

// Item 1 (Feb 2026) — Coverage dismissals. Lets a teacher "undo" the
// derived COVERED match for a single expectation code; the dismissal
// persists per-teacher and also drops the expectation from Live Reports.
export const dismissCoverage = (code) =>
  api
    .post("/report-cards/dismiss-coverage", {
      teacher_id: getTeacherId(),
      code,
    })
    .then((r) => r.data);

export const undismissCoverage = (code) =>
  api
    .delete(`/report-cards/dismiss-coverage/${encodeURIComponent(code)}`, {
      params: { teacher_id: getTeacherId() },
    })
    .then((r) => r.data);

// Phase 108.D — Live Reports.
// Generates 3 grounded parent-facing comment options for one subject +
// achievement level, anchored ONLY in expectations the teacher has
// marked Taught (and the source lessons that drove them).
export const generateLiveReport = ({
  subject,
  level,
  studentGrade = "",
  direction = null,
  nudge = "",
  priorComment = "",
  reportType = "",
} = {}) =>
  api
    .post(
      "/report-cards/live-report",
      {
        teacher_id: getTeacherId(),
        student_grade: studentGrade || undefined,
        subject,
        level,
        report_type: reportType,
        direction: direction || undefined,
        nudge: nudge || undefined,
        prior_comment: priorComment || undefined,
      },
      { timeout: 90000 }
    )
    .then((r) => r.data);

// Phase 48 — Google Drive (Save to Drive)
export const driveStatus = () =>
  api.get("/drive/status").then((r) => r.data);

export const driveConnect = () =>
  api.post("/drive/connect").then((r) => r.data);

export const driveDisconnect = () =>
  api.delete("/drive/disconnect").then((r) => r.data);

export const saveActivityToDrive = (activityId, format) =>
  api
    .post(`/activities/${activityId}/save-to-drive`, { format }, { timeout: 60000 })
    .then((r) => r.data);

export const refineLesson = async (activityId, message) => {
  const { data: started } = await api.post(
    `/activities/${activityId}/refine`,
    { message },
    { timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingRefine(activityId, jobId);
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      initialDelay: 3000,
      interval: 3000,
      errorPrefix: "Refinement",
    });
    return job; // { activity, reply, lesson_updated }
  } finally {
    clearPendingRefine(activityId);
  }
};

export const resumeRefineJob = async (activityId, jobId) => {
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      initialDelay: 0,
      interval: 3000,
      errorPrefix: "Refinement",
    });
    return job; // { activity, reply, lesson_updated }
  } finally {
    clearPendingRefine(activityId);
  }
};

export const exportUrl = (id, format) =>
  `${API}/activities/${id}/export?format=${format}`;

export const earlyAccessSignup = (email) =>
  api.post("/early-access", { email }).then((r) => r.data);

// ===== Planner (Phase 83a — Pro-only) =====
export const plannerAccess = () =>
  api.get("/planner/access").then((r) => r.data);

export const getPlannerSchedule = () =>
  api.get("/planner/schedule").then((r) => r.data);

export const savePlannerSchedule = (payload) =>
  api.put("/planner/schedule", payload).then((r) => r.data);

export const listPlannerEvents = (start, end) =>
  api.get("/planner/events", { params: { start, end } }).then((r) => r.data);

export const listPlannerEventsByActivity = (activityId) =>
  api.get(`/planner/events/by-activity/${activityId}`).then((r) => r.data);

export const createPlannerEvent = (payload) =>
  api.post("/planner/events", payload).then((r) => r.data);

export const updatePlannerEvent = (id, patch) =>
  api.patch(`/planner/events/${id}`, patch).then((r) => r.data);

export const deletePlannerEvent = (id) =>
  api.delete(`/planner/events/${id}`).then((r) => r.data);

// PA-day attachments (object storage)
export const uploadPlannerAttachment = (eventId, file, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`/planner/events/${eventId}/attachments`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    })
    .then((r) => r.data);
};

export const deletePlannerAttachment = (eventId, attachmentId) =>
  api.delete(`/planner/events/${eventId}/attachments/${attachmentId}`).then((r) => r.data);

export const attachmentUrl = (eventId, attachmentId) =>
  `${API}/planner/events/${eventId}/attachments/${attachmentId}`;


export const plannerNextSlot = (subject, after) =>
  api.get("/planner/next-slot", { params: { subject, after } }).then((r) => r.data);

export const plannerDayLabels = (dates) =>
  api.post("/planner/day-labels", { dates }).then((r) => r.data);

export const pushPlannerEvent = (id, cascade = true) =>
  api.post(`/planner/events/${id}/push`, { cascade }).then((r) => r.data);

export const schedulePlannerUnit = (activityId, startDate, preview = false) =>
  api
    .post("/planner/schedule-unit", {
      activity_id: activityId,
      start_date: startDate,
      preview,
    })
    .then((r) => r.data);

export const generateWorkLookFors = (payload) =>
  api.post("/planner/work-lookfors", payload).then((r) => r.data);


// ===== Build-from-resource =====
export const analyseResourceText = (text, filename) =>
  api
    .post("/resource/analyse", { text, filename }, { timeout: 60000 })
    .then((r) => r.data);

export const analyseResourceFile = async (file, onProgress) => {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post("/resource/analyse-file", form, {
    timeout: 180000,
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return data;
};

export const generateFromResource = async (payload, { onProgress } = {}) => {
  const { data: started } = await api.post(
    "/resource/generate",
    { ...payload, teacher_id: getTeacherId() },
    { timeout: 60000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingGenerate(jobId);
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Generation",
      onProgress,
    });
    return job.activity;
  } finally {
    clearPendingGenerate();
  }
};

// ===== Admin (gated by ADMIN_EMAILS server-side) =====
export const adminSummary = () =>
  api.get("/admin/summary").then((r) => r.data);

export const adminEarlyAccess = () =>
  api.get("/admin/early-access").then((r) => r.data);

export const adminEarlyAccessCsvUrl = () =>
  `${API}/admin/early-access?format=csv`;

export const adminDeleteEarlyAccess = (email) =>
  api
    .delete("/admin/early-access", { params: { email } })
    .then((r) => r.data);

export const adminDeleteLaunchWaitlist = (email) =>
  api
    .delete("/admin/launch-waitlist", { params: { email } })
    .then((r) => r.data);

export const adminLaunchWaitlist = () =>
  api.get("/admin/launch-waitlist").then((r) => r.data);

export const adminLaunchWaitlistCsvUrl = () =>
  `${API}/admin/launch-waitlist?format=csv`;

export const adminLaunchEmailPreview = () =>
  api.get("/admin/launch-email/preview").then((r) => r.data);

export const adminLaunchEmailSend = () =>
  api.post("/admin/launch-email/send").then((r) => r.data);

export const adminTeachers = (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== "" && v != null)
  ).toString();
  return api.get(`/admin/teachers${qs ? `?${qs}` : ""}`).then((r) => r.data);
};

export const adminTeachersCsvUrl = (params = {}) => {
  const qs = new URLSearchParams({
    format: "csv",
    ...Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== "" && v != null)
    ),
  }).toString();
  return `${API}/admin/teachers?${qs}`;
};

// --- User deletion tool ---
export const adminPreviewDeleteUsers = (user_ids) =>
  api.post("/admin/users/preview-delete", { user_ids }).then((r) => r.data);

export const adminDeleteUsers = (user_ids) =>
  api.post("/admin/users/delete", { user_ids }).then((r) => r.data);

export const adminRecentlyDeletedUsers = () =>
  api.get("/admin/users/recently-deleted").then((r) => r.data);

export const adminRestoreUsers = (user_ids) =>
  api.post("/admin/users/restore", { user_ids }).then((r) => r.data);

export const adminPurgeUsersNow = (user_ids) =>
  api.post("/admin/users/purge-now", { user_ids }).then((r) => r.data);

export const adminGrantMathAccess = (email, grant = true) =>
  api.post("/admin/math-engine-access", { email, grant }).then((r) => r.data);

export const adminSetUserTier = (email, tier_id, status = "active") =>
  api
    .post("/admin/set-user-tier", { email, tier_id, status })
    .then((r) => r.data);

export const adminRecentLessons = (limit = 50) =>
  api.get("/admin/lessons/recent", { params: { limit } }).then((r) => r.data);

export const adminGlobalCoverage = () =>
  api.get("/admin/coverage").then((r) => r.data);

// Tier 2 #4 (Feb 2026) — auto-pick adoption telemetry.
export const adminAutoPickStats = () =>
  api.get("/admin/auto-pick-stats").then((r) => r.data);

// Retention & Usage dashboard (Feb 2026).
export const adminRetention = (windowDays = 0) =>
  api
    .get("/admin/retention", { params: { window_days: windowDays } })
    .then((r) => r.data);

export const adminRetentionCsvUrl = () => `${API}/admin/retention.csv`;

// ===== Admin nudge emails (Phase 79) =====
export const adminNudgePreview = ({ skill, scope = "missed_only", custom_emails = null }) =>
  api.post("/admin/nudge/preview", { skill, scope, custom_emails }).then((r) => r.data);

export const adminNudgeSend = ({ skill, scope = "missed_only", custom_emails = null, subject, body_text, body_html = null, dry_run = false }) =>
  api.post("/admin/nudge/send", { skill, scope, custom_emails, subject, body_text, body_html, dry_run }).then((r) => r.data);

export const adminNudgeHistory = (limit = 50) =>
  api.get("/admin/nudge/history", { params: { limit } }).then((r) => r.data);



export const listStrategies = () =>
  api.get("/strategies").then((r) => r.data);

export const getStrategy = (slug) =>
  api.get(`/strategies/${slug}`).then((r) => r.data);

// ===== Site-wide password gate =====
export const siteAccessStatus = () =>
  api.get("/site-access/status").then((r) => r.data);

export const siteAccessUnlock = (password) =>
  api.post("/site-access/unlock", { password }).then((r) => r.data);


// ===== Math Engine — Unit Test + Review Sheet (Stage 10) =====
export const getMathUnitTestOptions = () =>
  api.get("/math-engine/unit-test/options").then((r) => r.data);

export const startMathUnitTest = async ({
  grade,
  unit_number,
  mini_unit_label,
  counts,
  types,
}, { onProgress } = {}) => {
  const { data: started } = await api.post(
    "/math-engine/unit-test",
    {
      grade,
      unit_number,
      mini_unit_label: mini_unit_label || "",
      counts,
      types,
      teacher_id: getTeacherId(),
    },
    { timeout: 30000 }
  );
  const jobId = started?.job_id;
  if (!jobId) throw new Error("Server did not return a job id");
  setPendingGenerate(jobId);
  try {
    const job = await pollJobUntilDone({
      jobId,
      pollUrl: `/jobs/${jobId}`,
      errorPrefix: "Math Engine unit test",
      onProgress,
    });
    // Backend returns { test, review } on the job; pass through unchanged.
    return job;
  } finally {
    clearPendingGenerate();
  }
};

export const getMathDiagnosticMidpointHint = (activityId) =>
  api
    .get(`/math-engine/diagnostic/${activityId}/midpoint-hint`)
    .then((r) => r.data);

// ============================================================
// Homepage hero preset — Lane 2 (Feb 2026).
// GET is public (unauthenticated); PUT is admin-gated.
// ============================================================
export const fetchHomepageHeroPreset = () =>
  api.get("/site-settings/homepage-hero").then((r) => r.data);

export const setHomepageHeroPreset = (preset) =>
  api
    .put("/admin/site-settings/homepage-hero", { preset })
    .then((r) => r.data);


// ============================================================
// Report-card nudge banner — Feb 2026 mid-month.
// GET is public (unauthenticated, mirrors the hero pair); PUT is
// admin-gated. Same setting drives Free + Basic audiences.
// ============================================================
export const fetchReportCardNudge = () =>
  api.get("/site-settings/report-card-nudge").then((r) => r.data);

export const setReportCardNudge = (position) =>
  api
    .put("/admin/site-settings/report-card-nudge", { position })
    .then((r) => r.data);


// ============================================================
// Package-cache maintenance — Jun 2026.
// The Complete Lesson Package PDF is content-hashed (no renderer/code
// version in the hash), so a render-code change (e.g. the phase-tag
// fix) does NOT auto-invalidate cached packages. After a deploy that
// changes package rendering, an admin clears the cache so teachers
// stop seeing pre-deploy bytes. Admin-gated on the backend
// (require_admin_user); touches ONLY package_cache.
// ============================================================
export const clearPackageCacheAll = () =>
  api.delete("/admin/package-cache").then((r) => r.data);

export const clearPackageCacheOne = (activityId, lessonIndex) =>
  api
    .delete(`/admin/package-cache/${encodeURIComponent(activityId)}`, {
      params: lessonIndex == null ? {} : { lesson_index: lessonIndex },
    })
    .then((r) => r.data);

// Force a fresh render of one lesson-package PDF from stored content, through
// the same path a normal download uses (regenerate=true → overwrites the
// cache blob). No LLM, no content change. Admin only.
export const rerenderLessonPackage = (activityId, lessonIndex) =>
  api
    .post(`/admin/re-render/${encodeURIComponent(activityId)}`, null, {
      params: lessonIndex == null ? {} : { lesson_index: lessonIndex },
    })
    .then((r) => r.data);
